import React, { useMemo } from "react";
import { Modal } from "antd";
import "./PackageAttractionDetailModal.scss";

function looksLikeHtml(s) {
  return typeof s === "string" && /<[a-z][\s\S]*>/i.test(s);
}

function galleryUrlsFromPayload(d) {
  if (!d || typeof d !== "object") return [];
  const raw = d.raw && typeof d.raw === "object" ? d.raw : {};
  const cat = raw.catalog && typeof raw.catalog === "object" ? raw.catalog : {};
  const g =
    cat.gallery_urls ??
    cat.galleryUrls ??
    raw.gallery_urls ??
    raw.galleryUrls ??
    d.galleryUrls;
  if (Array.isArray(g) && g.length) {
    return g.map((x) => (typeof x === "string" ? x : x?.url)).filter(Boolean);
  }
  const single =
    cat.image_url ||
    raw.image_url ||
    d.imageUrl ||
    (d.item && d.item.raw && d.item.raw.image_url);
  return single ? [String(single)] : [];
}

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   payload: {
 *     title?: string,
 *     description?: string,
 *     descriptionShort?: string,
 *     adultPrice?: number|null,
 *     childPrice?: number|null,
 *     childEligible?: boolean,
 *     category?: string,
 *     durationMinutes?: number|null,
 *     destinationLabel?: string,
 *     imageUrl?: string|null,
 *     raw?: Record<string, unknown>,
 *     item?: Record<string, unknown>,
 *   }|null,
 * }} props
 */
export default function PackageAttractionDetailModal({ open, onClose, payload }) {
  const gallery = useMemo(() => galleryUrlsFromPayload(payload), [payload]);

  if (!payload || typeof payload !== "object") {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        title="פרטי אטרקציה"
        destroyOnHidden
        width={720}
        className="pkg-attr-detail-modal"
      >
        <p className="pkg-attr-detail-modal__empty">אין נתונים להצגה.</p>
      </Modal>
    );
  }

  const title = String(payload.title || payload.item?.name || "אטרקציה").trim();
  const desc =
    String(payload.description || "").trim() ||
    String(payload.descriptionShort || payload.item?.descriptionShort || "").trim();
  const cat = String(payload.category || payload.item?.category || "").trim();
  const dur = payload.durationMinutes ?? payload.item?.durationMinutes;
  const dest = String(payload.destinationLabel || "").trim();

  const fmt = (n) =>
    n != null && Number.isFinite(Number(n))
      ? `${Math.round(Number(n)).toLocaleString()} ₪`
      : "—";

  const hero = gallery[0] || payload.imageUrl || null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={title}
      destroyOnHidden
      width={720}
      className="pkg-attr-detail-modal"
    >
      <div className="pkg-attr-detail-modal__body" dir="rtl">
        <div className="pkg-attr-detail-modal__hero-wrap">
          {hero ? (
            <img
              src={hero}
              alt=""
              className="pkg-attr-detail-modal__hero"
              loading="lazy"
            />
          ) : (
            <div className="pkg-attr-detail-modal__hero pkg-attr-detail-modal__hero--placeholder" aria-hidden>
              <span className="pkg-attr-detail-modal__placeholder-icon">🎯</span>
              <span>אטרקציה</span>
            </div>
          )}
        </div>

        {gallery.length > 1 ? (
          <ul className="pkg-attr-detail-modal__thumbs">
            {gallery.slice(0, 8).map((url, i) => (
              <li key={`${url}-${i}`}>
                <img src={url} alt="" loading="lazy" />
              </li>
            ))}
          </ul>
        ) : null}

        <dl className="pkg-attr-detail-modal__meta">
          {cat ? (
            <>
              <dt>קטגוריה</dt>
              <dd>{cat}</dd>
            </>
          ) : null}
          {dur != null && Number(dur) > 0 ? (
            <>
              <dt>משך משוער</dt>
              <dd>{dur} דק׳</dd>
            </>
          ) : null}
          {dest ? (
            <>
              <dt>יעד</dt>
              <dd>{dest}</dd>
            </>
          ) : null}
          <dt>מחיר למבוגר</dt>
          <dd>{fmt(payload.adultPrice)}</dd>
          <dt>מחיר לילד</dt>
          <dd>
            {payload.childEligible === false
              ? "לא זמין לילדים"
              : fmt(payload.childPrice)}
          </dd>
        </dl>

        {desc ? (
          looksLikeHtml(desc) ? (
            <div
              className="pkg-attr-detail-modal__desc"
              dangerouslySetInnerHTML={{ __html: desc }}
            />
          ) : (
            <p className="pkg-attr-detail-modal__desc pkg-attr-detail-modal__desc--plain">
              {desc}
            </p>
          )
        ) : (
          <p className="pkg-attr-detail-modal__muted">אין תיאור מפורט זמין.</p>
        )}
      </div>
    </Modal>
  );
}
